import { useCallback, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { useSocketStatus } from '@/lib/socket';
import { useAuthStore } from '@/store/auth.store';
import { areaForRole, homeForRole } from '@/lib/roles';
import { AppBar } from './AppBar';
import { Sidebar } from './Sidebar';

const RAIL_KEY = 'safepass.sidebarOpen';

/*
  The signed-in shell: a fixed command bar, a collapsible rail under it, and the
  page in what is left.

  The rail's state is persisted. Someone who works all day on a 1366px laptop
  collapses it once and means it — having it spring back open on every reload is
  the kind of small daily friction that makes a tool feel like it is not theirs.
*/
export function Layout() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.user?.role);
  const connected = useSocketStatus();

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(RAIL_KEY);
      return saved === null ? true : (JSON.parse(saved) as boolean);
    } catch {
      return true;
    }
  });

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(RAIL_KEY, JSON.stringify(next));
      } catch {
        /* Private browsing — the rail still works, it just will not be remembered. */
      }
      return next;
    });
  }, []);

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  /* A parent or gate officer who deep-links into the dashboard is sent to their
     own app. Every screen here would otherwise render and then 403. */
  /*
    Teachers share this shell but see only the dismissal board — the nav filters
    itself by role below, and every other route under it is admin-gated on the
    API anyway. Bouncing them out entirely would mean building a second shell
    for one screen.
  */
  const area = areaForRole(role);
  if (area !== 'admin' && area !== 'teacher') return <Navigate to={homeForRole(role)} replace />;

  return (
    <div className="min-h-screen bg-background">
      <AppBar sidebarOpen={sidebarOpen} onToggleSidebar={toggleSidebar} connected={connected} />
      <Sidebar open={sidebarOpen} />

      <main
        className={cn(
          'pt-bar transition-[margin] duration-200 ease-out',
          sidebarOpen ? 'ml-rail' : 'ml-rail-collapsed',
        )}
      >
        {/* The canvas. Cards inside sit on --card, one step above it — which is
            what lets every panel in the product go without a shadow. */}
        <div className="min-h-[calc(100vh-theme(spacing.bar))] p-3.5">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
